import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import {
    type Allergen,
    AllergenSchema,
    type IngredientAdmin,
    type IngredientMeasureType,
    type Ingredients,
    type IngredientUnit,
    MEASURE_TYPE_UNITS } from '@cookspace-shared-contracts/core';
import { ChevronLeft, ChevronRight, Pencil, Plus, Search, Trash2 } from 'lucide-react';

import { useListIngredientGroupsQuery } from '@/api/ingredientGroupsApi';
import {
    useCreateIngredientMutation,
    useDeleteIngredientMutation,
    useListIngredientsQuery,
    useUpdateIngredientMutation
} from '@/api/ingredientsApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { Switch } from '@/components/Switch';

const PAGE_SIZE = 20;

/** Человекочитаемые единицы измерения. */
const UNIT_LABELS: Record<IngredientUnit, string> = {
    g: 'г',
    kg: 'кг',
    ml: 'мл',
    l: 'л',
    pcs: 'шт'
};

/** Человекочитаемые типы измерения. */
const MEASURE_TYPE_LABELS: Record<IngredientMeasureType, string> = {
    mass: 'Масса',
    volume: 'Объём',
    count: 'Количество'
};

const MEASURE_TYPE_OPTIONS: Array<{ value: IngredientMeasureType; label: string }> = [
    { value: 'mass', label: 'Масса' },
    { value: 'volume', label: 'Объём' },
    { value: 'count', label: 'Количество' }
];

/** RU-лейблы аллергенов (14 групп ТР ТС 022). */
const ALLERGEN_LABELS: Record<Allergen, string> = {
    gluten: 'Глютен',
    crustaceans: 'Ракообразные',
    eggs: 'Яйца',
    fish: 'Рыба',
    peanuts: 'Арахис',
    soybeans: 'Соя',
    milk: 'Молоко',
    nuts: 'Орехи',
    celery: 'Сельдерей',
    mustard: 'Горчица',
    sesame: 'Кунжут',
    sulphites: 'Сульфиты',
    lupin: 'Люпин',
    molluscs: 'Моллюски'
};

/** Канонический порядок аллергенов берём из enum-схемы контракта (без дрейфа). */
const ALLERGEN_VALUES = AllergenSchema.options;

interface FormState {
    groupId: string;
    name: string;
    unit: IngredientUnit;
    measureType: IngredientMeasureType;
    costPerUnit: string;
    allergens: Allergen[];
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    groupId?: string;
    costPerUnit?: string;
    unit?: string;
}

const EMPTY_FORM: FormState = {
    groupId: '',
    name: '',
    unit: 'g',
    measureType: 'mass',
    costPerUnit: '',
    allergens: [],
    isActive: true
};

/** 409 от backend = конфликт уникальности имени или ингредиент используется. */
function isConflict(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        (error as { status?: unknown }).status === 409
    );
}

const TABLE_GRID =
    'grid grid-cols-[80px_minmax(0,1.4fr)_minmax(0,1fr)_56px_104px_96px_minmax(0,1.3fr)_64px_96px] gap-2';

export function IngredientsPage(): ReactElement {
    const [filterGroupId, setFilterGroupId] = useState('');
    const [filterActiveOnly, setFilterActiveOnly] = useState(false);
    const [filterAllergen, setFilterAllergen] = useState<Allergen | ''>('');
    const [qInput, setQInput] = useState('');
    const [q, setQ] = useState('');
    const [offset, setOffset] = useState(0);

    const queryArg: Ingredients.GetIngredientsRequest = useMemo(() => {
        const arg: Ingredients.GetIngredientsRequest = { limit: PAGE_SIZE, offset };
        if (filterGroupId) {
            arg.groupId = filterGroupId;
        }
        if (q) {
            arg.q = q;
        }
        if (filterActiveOnly) {
            arg.isActive = true;
        }
        if (filterAllergen) {
            arg.allergen = filterAllergen;
        }
        return arg;
    }, [offset, filterGroupId, q, filterActiveOnly, filterAllergen]);

    const { data, isLoading, isError, isFetching } = useListIngredientsQuery(queryArg);
    const { data: groupsData } = useListIngredientGroupsQuery({});

    const [createIngredient, { isLoading: isCreating }] = useCreateIngredientMutation();
    const [updateIngredient, { isLoading: isUpdating }] = useUpdateIngredientMutation();
    const [deleteIngredient] = useDeleteIngredientMutation();

    const [isModalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<IngredientAdmin | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<IngredientAdmin | null>(null);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const groups = groupsData?.items ?? [];

    // Карта id→название для подписи группы в строках таблицы. Завязана на
    // groupsData (а не на пересоздаваемый каждый рендер массив groups).
    const groupNameById = useMemo(
        () => new Map((groupsData?.items ?? []).map((g) => [g.id, g.name])),
        [groupsData]
    );

    const groupFilterOptions = groups.map((g) => ({ value: g.id, label: g.name }));
    const allergenFilterOptions = ALLERGEN_VALUES.map((a) => ({
        value: a,
        label: ALLERGEN_LABELS[a]
    }));
    const unitOptions = MEASURE_TYPE_UNITS[form.measureType].map((u) => ({
        value: u,
        label: UNIT_LABELS[u]
    }));

    const openCreate = (): void => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const openEdit = (ingredient: IngredientAdmin): void => {
        setEditing(ingredient);
        setForm({
            groupId: ingredient.groupId,
            name: ingredient.name,
            unit: ingredient.unit,
            measureType: ingredient.measureType,
            costPerUnit: ingredient.costPerUnit === null ? '' : String(ingredient.costPerUnit),
            allergens: ingredient.allergens ?? [],
            isActive: ingredient.isActive
        });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const validate = (): boolean => {
        const errors: FormErrors = {};
        if (!form.name.trim()) {
            errors.name = 'Название обязательно';
        }
        if (!form.groupId) {
            errors.groupId = 'Выберите группу';
        }
        if (form.costPerUnit.trim() !== '') {
            const cost = Number(form.costPerUnit);
            if (!Number.isFinite(cost) || cost < 0) {
                errors.costPerUnit = 'Себестоимость — число не меньше 0';
            }
        }
        if (!MEASURE_TYPE_UNITS[form.measureType].includes(form.unit)) {
            errors.unit = 'Единица не соответствует типу измерения';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setSubmitError(null);
        if (!validate()) {
            return;
        }

        const costPerUnit = form.costPerUnit.trim() === '' ? null : Number(form.costPerUnit);

        try {
            if (editing) {
                const body: Ingredients.UpdateIngredientRequest = {
                    groupId: form.groupId,
                    name: form.name.trim(),
                    unit: form.unit,
                    measureType: form.measureType,
                    costPerUnit,
                    allergens: form.allergens,
                    isActive: form.isActive
                };
                await updateIngredient({ id: editing.id, body }).unwrap();
            } else {
                const body: Ingredients.CreateIngredientRequest = {
                    groupId: form.groupId,
                    name: form.name.trim(),
                    unit: form.unit,
                    measureType: form.measureType,
                    costPerUnit,
                    allergens: form.allergens,
                    isActive: form.isActive
                };
                await createIngredient(body).unwrap();
            }
            setModalOpen(false);
        } catch (error) {
            setSubmitError(
                isConflict(error)
                    ? 'Ингредиент с таким названием уже существует'
                    : 'Не удалось сохранить ингредиент'
            );
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteIngredient({ id: pendingDelete.id }).unwrap();
            setPendingDelete(null);
        } catch (error) {
            setPendingDelete(null);
            setPageError(
                isConflict(error)
                    ? 'Ингредиент используется в составе блюд — деактивируйте его вместо удаления'
                    : 'Не удалось удалить ингредиент'
            );
        }
    };

    const handleToggleActive = async (
        ingredient: IngredientAdmin,
        next: boolean
    ): Promise<void> => {
        try {
            await updateIngredient({ id: ingredient.id, body: { isActive: next } }).unwrap();
        } catch {
            setPageError('Не удалось изменить статус');
        }
    };

    const handleMeasureTypeChange = (measureType: IngredientMeasureType): void => {
        // Единица всегда согласована с типом измерения — при смене типа
        // сбрасываем единицу на первую допустимую (зеркало MEASURE_TYPE_UNITS).
        setForm({ ...form, measureType, unit: MEASURE_TYPE_UNITS[measureType][0] });
    };

    const submitSearch = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setQ(qInput.trim());
        setOffset(0);
    };

    const changeGroupFilter = (value: string): void => {
        setFilterGroupId(value);
        setOffset(0);
    };

    const changeAllergenFilter = (value: string): void => {
        setFilterAllergen(value as Allergen | '');
        setOffset(0);
    };

    const changeActiveFilter = (value: boolean): void => {
        setFilterActiveOnly(value);
        setOffset(0);
    };

    const toggleAllergen = (allergen: Allergen): void => {
        setForm((prev) => ({
            ...prev,
            allergens: prev.allergens.includes(allergen)
                ? prev.allergens.filter((a) => a !== allergen)
                : [...prev.allergens, allergen]
        }));
    };

    const rangeFrom = total === 0 ? 0 : offset + 1;
    const rangeTo = offset + items.length;
    const canPrev = offset > 0;
    const canNext = offset + PAGE_SIZE < total;

    return (
        <div data-testid="ingredients-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Ингредиенты</h1>
                <button
                    type="button"
                    onClick={openCreate}
                    data-testid="ingredient-create-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать ингредиент
                </button>
            </div>

            <div className="flex flex-wrap items-end gap-4 rounded border border-gray-200 bg-white p-3">
                <form onSubmit={submitSearch} className="flex items-end gap-2" data-testid="ingredient-search-form">
                    <div className="w-64">
                        <Input
                            label="Поиск по названию"
                            value={qInput}
                            onChange={(e) => setQInput(e.target.value)}
                            placeholder="Например, томат"
                            data-testid="ingredient-search-input"
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid="ingredient-search-submit"
                        className="inline-flex h-[38px] items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 transition hover:bg-gray-100"
                    >
                        <Search size={14} />
                        Найти
                    </button>
                </form>
                <div className="w-56">
                    <Select
                        label="Группа"
                        value={filterGroupId}
                        onChange={(e) => changeGroupFilter(e.target.value)}
                        placeholder="Все группы"
                        options={groupFilterOptions}
                        data-testid="ingredient-filter-group"
                    />
                </div>
                <div className="w-56">
                    <Select
                        label="Аллерген"
                        value={filterAllergen}
                        onChange={(e) => changeAllergenFilter(e.target.value)}
                        placeholder="Все аллергены"
                        options={allergenFilterOptions}
                        data-testid="ingredient-filter-allergen"
                    />
                </div>
                <div className="pb-2">
                    <Switch
                        checked={filterActiveOnly}
                        onChange={changeActiveFilter}
                        label="Только активные"
                        testId="ingredient-filter-active"
                    />
                </div>
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="ingredients-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="ingredients-error">
                    Не удалось загрузить ингредиенты
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="ingredients-table"
                >
                    <div
                        className={`${TABLE_GRID} border-b border-gray-200 bg-gray-50 px-2 py-2 text-xs font-medium uppercase text-gray-600`}
                    >
                        <span>ID</span>
                        <span>Название</span>
                        <span>Группа</span>
                        <span>Ед.</span>
                        <span>Тип</span>
                        <span>Себест., ₽</span>
                        <span>Аллергены</span>
                        <span>Актив.</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Ингредиенты не найдены
                        </div>
                    )}

                    {items.map((ingredient) => (
                        <div
                            key={ingredient.id}
                            className={`${TABLE_GRID} items-center border-b border-gray-100 px-2 py-2 text-sm last:border-b-0`}
                            data-testid={`ingredient-row-${ingredient.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {ingredient.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">{ingredient.name}</span>
                            <span className="truncate text-gray-600">
                                {groupNameById.get(ingredient.groupId) ?? '—'}
                            </span>
                            <span className="text-gray-700">{UNIT_LABELS[ingredient.unit]}</span>
                            <span className="text-gray-700">
                                {MEASURE_TYPE_LABELS[ingredient.measureType]}
                            </span>
                            <span className="text-gray-700">
                                {ingredient.costPerUnit === null ? '—' : ingredient.costPerUnit}
                            </span>
                            <span className="flex flex-wrap gap-1">
                                {ingredient.allergens && ingredient.allergens.length > 0 ? (
                                    ingredient.allergens.map((a) => (
                                        <span
                                            key={a}
                                            className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                                        >
                                            {ALLERGEN_LABELS[a]}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                )}
                            </span>
                            <span>
                                <Switch
                                    checked={ingredient.isActive}
                                    onChange={(next) => handleToggleActive(ingredient, next)}
                                    testId={`ingredient-active-${ingredient.id}`}
                                />
                            </span>
                            <span className="flex justify-end gap-1">
                                <button
                                    type="button"
                                    onClick={() => openEdit(ingredient)}
                                    data-testid={`ingredient-edit-${ingredient.id}`}
                                    className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                    aria-label="Редактировать"
                                >
                                    <Pencil size={14} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPendingDelete(ingredient)}
                                    data-testid={`ingredient-delete-${ingredient.id}`}
                                    className="rounded p-1.5 text-red-600 transition hover:bg-red-50"
                                    aria-label="Удалить"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </span>
                        </div>
                    ))}

                    <div
                        className="flex items-center justify-between border-t border-gray-200 px-3 py-2 text-sm text-gray-600"
                        data-testid="ingredients-pagination"
                    >
                        <span>
                            {rangeFrom}–{rangeTo} из {total}
                        </span>
                        <div className="flex gap-1">
                            <button
                                type="button"
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                disabled={!canPrev || isFetching}
                                data-testid="ingredients-prev-page"
                                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 transition hover:bg-gray-100 disabled:opacity-40"
                            >
                                <ChevronLeft size={14} />
                                Назад
                            </button>
                            <button
                                type="button"
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                                disabled={!canNext || isFetching}
                                data-testid="ingredients-next-page"
                                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 transition hover:bg-gray-100 disabled:opacity-40"
                            >
                                Вперёд
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? 'Редактировать ингредиент' : 'Новый ингредиент'}
                testId="ingredient-modal"
            >
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="ingredient-form">
                    <Input
                        label="Название"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        error={formErrors.name}
                        data-testid="ingredient-form-name"
                        autoFocus
                    />
                    <div>
                        <Select
                            label="Группа"
                            value={form.groupId}
                            onChange={(e) => setForm({ ...form, groupId: e.target.value })}
                            placeholder="Выберите группу"
                            options={groupFilterOptions}
                            error={formErrors.groupId}
                            data-testid="ingredient-form-group"
                        />
                        {groups.length === 0 && (
                            <p className="mt-1 text-xs text-amber-600">
                                Сначала создайте хотя бы одну группу ингредиентов.
                            </p>
                        )}
                    </div>
                    <Select
                        label="Тип измерения"
                        value={form.measureType}
                        onChange={(e) =>
                            handleMeasureTypeChange(e.target.value as IngredientMeasureType)
                        }
                        options={MEASURE_TYPE_OPTIONS}
                        data-testid="ingredient-form-measure-type"
                    />
                    <Select
                        label="Единица измерения"
                        value={form.unit}
                        onChange={(e) => setForm({ ...form, unit: e.target.value as IngredientUnit })}
                        options={unitOptions}
                        error={formErrors.unit}
                        data-testid="ingredient-form-unit"
                    />
                    <Input
                        label="Себестоимость за единицу, ₽ (опционально)"
                        type="number"
                        min={0}
                        step="0.01"
                        value={form.costPerUnit}
                        onChange={(e) => setForm({ ...form, costPerUnit: e.target.value })}
                        error={formErrors.costPerUnit}
                        data-testid="ingredient-form-cost-per-unit"
                    />
                    <div>
                        <span className="mb-1 block text-sm text-gray-700">Аллергены</span>
                        <div
                            className="flex flex-wrap gap-2"
                            data-testid="ingredient-form-allergens"
                        >
                            {ALLERGEN_VALUES.map((allergen) => {
                                const selected = form.allergens.includes(allergen);
                                return (
                                    <button
                                        key={allergen}
                                        type="button"
                                        onClick={() => toggleAllergen(allergen)}
                                        aria-pressed={selected}
                                        data-testid={`ingredient-form-allergen-${allergen}`}
                                        className={`rounded-full border px-3 py-1 text-xs transition ${
                                            selected
                                                ? 'border-amber-500 bg-amber-100 text-amber-800'
                                                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        {ALLERGEN_LABELS[allergen]}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <Switch
                        checked={form.isActive}
                        onChange={(next) => setForm({ ...form, isActive: next })}
                        label="Активен"
                        testId="ingredient-form-is-active"
                    />

                    {submitError !== null && submitError !== '' && (
                        <div className="text-sm text-red-600" data-testid="ingredient-form-error">
                            {submitError}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setModalOpen(false)}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || isUpdating}
                            data-testid="ingredient-form-submit"
                            className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            {editing ? 'Сохранить' : 'Создать'}
                        </button>
                    </div>
                </form>
            </Modal>

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title="Удалить ингредиент"
                message={
                    pendingDelete
                        ? `Удалить ингредиент «${pendingDelete.name}»? Действие необратимо.`
                        : ''
                }
                confirmLabel="Удалить"
                danger
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
